import { DotNetBridge, IWritableSettings } from '@barthauer/abc/core';
import { addDays, differenceInSeconds } from 'date-fns';
import { AuthenticationLevelMode, IdentityMode, LoginInfo, PasswordRequirementsInfo } from './login-info.model';

export enum LoginSetting {
  access_token,
  refresh_token,
  token_type,
  expires,
  expires_in,
  issued,
  userName,
}

export interface ILoginSettings {
  readonly isAnonymousLogin: boolean;
  readonly access_token: string;
  readonly refresh_token: string;
  readonly token_type: string;
  readonly expires: Date;
  readonly expires_in: number;
  readonly issued: Date;
  readonly userName: string;
  readonly isExpired: boolean;
  readonly isLoginRequired: boolean;

  toLoginInfo(
    id?: string,
    name?: string,
    displayName?: string,
    isAdmin?: boolean,
    needsPasswordChange?: boolean,
    emailAddress?: string,
    secondFactorMode?: AuthenticationLevelMode,
    emailIdentityMode?: IdentityMode,
    phoneIdentityMode?: IdentityMode,
    appTotpIdentityMode?: IdentityMode,
    passwordRequirements?: PasswordRequirementsInfo,
  ): LoginInfo;
}

export class LoginSettings implements ILoginSettings, IWritableSettings<ILoginSettings> {
  constructor(loginSettings?: ILoginSettings) {
    this.load(loginSettings);
  }

  private _isAnonymousLogin: boolean;
  public get isAnonymousLogin(): boolean {
    return this._isAnonymousLogin;
  }

  public access_token: string;
  public refresh_token: string;
  public token_type: string;
  public expires: Date;
  public expires_in: number;
  public issued: Date;
  public userName: string;

  public get isExpired(): boolean {
    return this.isLoginRequired ? this.expires < new Date() : false;
  }

  public get isLoginRequired(): boolean {
    return !this.isAnonymousLogin && !DotNetBridge.hasContext;
  }

  public static CreateAnonymousLoginSettings(): LoginSettings {
    const anonymousLoginSettings: LoginSettings = new LoginSettings();
    anonymousLoginSettings._isAnonymousLogin = true;
    anonymousLoginSettings.issued = new Date();
    anonymousLoginSettings.expires = addDays(anonymousLoginSettings.issued, 1);
    anonymousLoginSettings.expires_in = Math.abs(
      differenceInSeconds(anonymousLoginSettings.expires, anonymousLoginSettings.issued)
    );
    return anonymousLoginSettings;
  }

  public toLoginInfo(
    userId?: string,
    name?: string,
    displayName?: string,
    isAdmin?: boolean,
    needsPasswordChange?: boolean,
    emailAddress?: string,
    secondFactorMode?: AuthenticationLevelMode,
    emailIdentityMode?: IdentityMode,
    phoneIdentityMode?: IdentityMode,
    appTotpIdentityMode?: IdentityMode,
    passwordRequirements?: PasswordRequirementsInfo
  ): LoginInfo {
    return new LoginInfo(
      this.isAnonymousLogin,
      this.userName,
      this.issued,
      this.expires,
      userId,
      name,
      displayName,
      isAdmin,
      needsPasswordChange,
      emailAddress,
      secondFactorMode,
      emailIdentityMode,
      phoneIdentityMode,
      appTotpIdentityMode,
      passwordRequirements
    );
  }
  public load(loginSettings: ILoginSettings): void {
    if (loginSettings) {
      this._isAnonymousLogin = loginSettings.isAnonymousLogin;
      this.expires = loginSettings.expires;
      this.expires_in = loginSettings.expires_in;
      this.issued = loginSettings.issued;
      this.userName = loginSettings.userName;

      if (!loginSettings.isAnonymousLogin) {
        this.access_token = loginSettings.access_token;
        this.refresh_token = loginSettings.refresh_token;
        this.token_type = loginSettings.token_type;
      }
    } else {
      this._isAnonymousLogin = false;
    }
  }
}
